import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { RecognitionBadge } from "@/components/recognition-badge/RecognitionBadge.tsx";
import { Search } from "@/components/search/Search.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { RecognitionActionsMenu } from "@/pages/settings/recognitions/widgets/RecognitionActionsMenu.tsx";
import { RecognitionEditModal } from "@/pages/settings/recognitions/widgets/RecognitionEditModal.tsx";
import { RecognitionPreviewModal } from "@/pages/settings/recognitions/widgets/RecognitionPreviewModal.tsx";
import { RecognitionSortModal } from "@/pages/settings/recognitions/widgets/RecognitionSortModal.tsx";
import { useRecognitionCategory, useRecognitions } from "@/queries/use-recognitions.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { ApiRecognition } from "@/types/recognitions/recognition.types.ts";
import {
  Button,
  EmptyStateBase,
  Page,
  Stack,
  Table,
  TableCell,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

export function RecognitionCategoryPage() {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { openModal } = useModals();
  const { id } = useParams();

  const { data: category } = useRecognitionCategory(id ? Number(id) : undefined);

  const { data: recognitions, isLoading } = useRecognitions(
    id ? Number(id) : undefined,
    id !== undefined
  );

  useTitle(category?.title ?? t("recognition:settings_title"), t("common:settings"));

  const [search, setSearch] = useState("");

  const rows = useMemo<ApiRecognition[]>(() => {
    const query = search.trim().toLowerCase();
    return (recognitions ?? []).filter(
      (item) => !query || item.title.toLowerCase().includes(query)
    );
  }, [recognitions, search]);

  const columns = useMemo<ColumnDef<ApiRecognition>[]>(
    () => [
      {
        header: t("common:title"),
        accessorKey: "title",
        cell: (row) => (
          <TableCell>
            <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
              <RecognitionBadge template={row.row.original} size={"xs"} />
              {row.row.original.title}
            </Stack>
          </TableCell>
        ),
        ...ColumnSizes.middle,
      },
      {
        header: t("recognition:column_created_by"),
        accessorKey: "created_by",
        cell: (row) => {
          const createdBy = row.row.original.created_by;
          return (
            <TableCell>
              {createdBy && (
                <EmployeeHoverCard
                  employee={createdBy}
                  position={getEmployeePositionTitle(createdBy)}
                >
                  <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
                    <EmployeeAvatar employee={createdBy} size={"md"} />
                    {renderEmployeeName(createdBy)}
                  </Stack>
                </EmployeeHoverCard>
              )}
            </TableCell>
          );
        },
        ...ColumnSizes.standard,
      },
      {
        header: t("recognition:column_awarded_to"),
        accessorKey: "granted_count",
        cell: (row) => (
          <TableCell variant={"secondary"}>
            {t("recognition:granted_count", { count: row.row.original.granted_count })}
          </TableCell>
        ),
        ...ColumnSizes.narrow,
      },
    ],
    [t, renderEmployeeName]
  );

  return (
    <Page>
      <Page.Header
        title={category?.title ?? ""}
        showBack={true}
        navigateTo={"/settings/recognitions"}
      >
        <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
          {category && rows.length > 1 && (
            <Button
              variant={"secondary"}
              onClick={() => openModal(RecognitionSortModal, { categoryId: category.id })}
            >
              {t("common:reorder")}
            </Button>
          )}
          <Button onClick={() => openModal(RecognitionEditModal, { categoryId: category?.id })}>
            {t("recognition:recognition_create_title")}
          </Button>
        </Stack>
      </Page.Header>
      <Page.Content>
        <Stack className={"px-16"} gap={"md"}>
          <Search
            value={search}
            onInput={(e) => setSearch(e.currentTarget.value)}
            clearable={true}
            onClear={() => setSearch("")}
            placeholder={t("recognition:search_placeholder")}
          />
          <Table
            data={rows}
            columns={columns}
            showSkeleton={isLoading}
            emptyStateComponent={
              <EmptyStateBase
                title={t("recognition:category_recognitions_empty_title")}
                description={t("recognition:category_recognitions_empty_description")}
              />
            }
            renderRowAction={(row) => {
              const recognition = row.original;
              return (
                <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
                  <Button
                    variant={"secondary"}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault();
                      openModal(RecognitionPreviewModal, { recognition });
                    }}
                  >
                    {t("common:preview")}
                  </Button>
                  <RecognitionActionsMenu recognition={recognition} />
                </Stack>
              );
            }}
          />
        </Stack>
      </Page.Content>
    </Page>
  );
}
